import { useEffect, useMemo, useRef, useState } from 'react'

import {
  createViewDay,
  createViewMonthGrid,
  createViewWeek,
  createViewList,
  type CalendarEventExternal,
} from '@schedule-x/calendar'
import { ScheduleXCalendar, useCalendarApp } from '@schedule-x/react'
import '@schedule-x/theme-default/dist/index.css'
import { AnchoredPopover } from '@/components/AnchoredPopover'
import { BookingCard, type BookingRow } from '@/components/BookingCard'

type CalendarView = ReturnType<typeof createViewMonthGrid>

export interface BookingCalendarEvent {
  id: string
  /**
   * ISO date in the local timezone (YYYY-MM-DD).
   */
  date: string
  /**
   * Optional start time in 24h format (HH:mm).
   */
  startTime?: string
  /**
   * Optional end time in 24h format (HH:mm).
   * If omitted, a 1-hour duration is assumed.
   */
  endTime?: string
  title: string
  /**
   * Optional booking details used for the popover card.
   * When provided, clicking the event will show a BookingCard.
   */
  booking?: BookingRow
}

export interface BookingsCalendarProps {
  events: BookingCalendarEvent[]
  getAuthorDisplayName?: (authorId: string) => string
  /**
   * When true, only show the list view (no month/week/day views).
   */
  listOnly?: boolean
  /**
   * Optional handler for when a booking's "Modify" action is triggered
   * from the popover card.
   */
  onModifyCallout?: (booking: BookingRow) => void
  /**
   * Optional handler for when a booking is marked as ready to invoice
   * from the popover card.
   */
  onMarkReadyToInvoice?: (booking: BookingRow) => void
  /**
   * Optional handler for when a booking is cancelled
   * from the popover card.
   */
  onCancelCallout?: (booking: BookingRow) => void
  /**
   * Optional handler for creating a callout directly from a month-grid date.
   */
  onCreateCalloutAtDate?: (date: string) => void
  /**
   * Optional handler for creating a session directly from a month-grid date.
   */
  onCreateSessionAtDate?: (date: string) => void
}

const toCalendarEvents = (
  events: BookingCalendarEvent[],
): CalendarEventExternal[] => {
  const temporal = (globalThis as any).Temporal

  if (!temporal) {
    return []
  }

  const timezone = temporal.Now.zonedDateTimeISO().timeZoneId

  return events.map((event) => {
    const isInvoiced = event.booking?.calloutStatus === 'Invoiced'
    const isSession = event.booking?.serviceType === 'Session'
    const isSitDownSession = event.booking?.sessionSubtype === 'Sit-down Session'
    const calendarId = isInvoiced
      ? 'finalized'
      : isSession
        ? isSitDownSession
          ? 'session_sitdown'
          : 'session_teamviewer'
        : 'callout'

    if (!event.startTime) {
      const date = temporal.PlainDate.from(event.date)

      return {
        id: event.id,
        start: date,
        end: date,
        title: event.title,
        calendarId,
      }
    }

    const start = temporal.ZonedDateTime.from(
      `${event.date}T${event.startTime}:00[${timezone}]`,
    )

    const end = event.endTime
      ? temporal.ZonedDateTime.from(
          `${event.date}T${event.endTime}:00[${timezone}]`,
        )
      : start.add({ hours: 1 })

    return {
      id: event.id,
      start,
      end,
      title: event.title,
      calendarId,
    }
  })
}

export function BookingsCalendar({
  events,
  getAuthorDisplayName,
  listOnly = false,
  onModifyCallout,
  onMarkReadyToInvoice,
  onCancelCallout,
  onCreateCalloutAtDate,
  onCreateSessionAtDate,
}: BookingsCalendarProps) {
  const temporal = (globalThis as any).Temporal
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null)
  const [pinnedNewBookingDate, setPinnedNewBookingDate] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const newBookingMenuRef = useRef<{
    date: string
    anchorRect: DOMRect
  } | null>(null)
  const [newBookingMenu, setNewBookingMenu] = useState<{
    date: string
    anchorRect: DOMRect
  } | null>(null)

  const clearPinnedNewBookingTrigger = () => {
    setPinnedNewBookingDate(null)
  }

  useEffect(() => {
    newBookingMenuRef.current = newBookingMenu
  }, [newBookingMenu])

  useEffect(() => {
    if (!newBookingMenu) return

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return

      if (target.closest('.oc-new-booking-menu')) {
        return
      }

      if (target.closest('.oc-new-booking-trigger')) {
        return
      }

      clearPinnedNewBookingTrigger()
      newBookingMenuRef.current = null
      setNewBookingMenu(null)
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [newBookingMenu])

  const gridHeight =
    typeof window === 'undefined'
      ? 600
      : Math.max(480, window.innerHeight - 240)

  const views = useMemo(() => {
    const monthGridView = createViewMonthGrid()
    const weekView = createViewWeek()
    const dayView = createViewDay()
    const listView = createViewList()

    if (listOnly) {
      return [listView] as [CalendarView, ...CalendarView[]]
    }

    return [
      monthGridView,
      weekView,
      dayView,
      listView,
    ] as [CalendarView, ...CalendarView[]]
  }, [listOnly])

  const initialCalendarEvents = useMemo(
    () => toCalendarEvents(events),
    [events],
  )

  const selectedDate = temporal
    ? temporal.Now.plainDateISO()
    : undefined

  const calendarApp = useCalendarApp(
    {
      views,
      // Schedule-X doesn't currently ship an en-ZA locale pack; using en-US
      // avoids raw template tokens like "+ {{n}} events" leaking into UI.
      locale: 'en-US',
      timezone: 'Africa/Johannesburg',
      defaultView: views[0]?.name,
      calendars: {
        callout: {
          colorName: 'callout',
          lightColors: {
            main: '#6d28d9',
            container: '#f3e8ff',
            onContainer: '#3b0764',
          },
        },
        session_teamviewer: {
          colorName: 'sessionteamviewer',
          lightColors: {
            main: '#2563eb',
            container: '#dbeafe',
            onContainer: '#1e3a8a',
          },
        },
        session_sitdown: {
          colorName: 'sessionsitdown',
          lightColors: {
            main: '#059669',
            container: '#d1fae5',
            onContainer: '#065f46',
          },
        },
        finalized: {
          colorName: 'finalized',
          lightColors: {
            main: '#64748b',
            container: '#f1f5f9',
            onContainer: '#334155',
          },
        },
      },
      events: initialCalendarEvents,
      dayBoundaries: {
        start: '06:00',
        end: '19:00',
      },
      selectedDate,
      weekOptions: {
        gridHeight,
        nDays: 7,
        eventWidth: 100,
        timeAxisFormatOptions: {
          hour: 'numeric',
          minute: '2-digit',
        },
        eventOverlap: true,
        gridStep: 30,
      },
      monthGridOptions: {
        nEventsPerDay: 12,
      },
      callbacks: {
        onClickDate(date, uiEvent) {
          const target =
            uiEvent && 'target' in uiEvent
              ? (uiEvent.target as HTMLElement | null)
              : null

          const trigger = target?.closest(
            '.oc-new-booking-trigger',
          ) as HTMLElement | null

          if (trigger) {
            uiEvent?.preventDefault?.()
            uiEvent?.stopPropagation?.()

            const clickedDateKey = date.toString()

            if (newBookingMenuRef.current?.date === clickedDateKey) {
              clearPinnedNewBookingTrigger()
              newBookingMenuRef.current = null
              setNewBookingMenu(null)
              return
            }

            setPinnedNewBookingDate(clickedDateKey)
            const nextMenu = {
              date: clickedDateKey,
              anchorRect: trigger.getBoundingClientRect(),
            }
            newBookingMenuRef.current = nextMenu
            setNewBookingMenu(nextMenu)
            return
          }

          clearPinnedNewBookingTrigger()
          newBookingMenuRef.current = null
          setNewBookingMenu(null)
        },
        onEventClick(externalEvent, uiEvent) {
          setSelectedEventId(String(externalEvent.id))

          const target =
            uiEvent && 'target' in uiEvent
              ? (uiEvent.target as HTMLElement | null)
              : null

          const eventElement = target
            ? (target.closest('.sx__event') as HTMLElement | null)
            : null

          if (eventElement) {
            setAnchorRect(eventElement.getBoundingClientRect())
          } else {
            setAnchorRect(null)
          }
        },
      },
    })

  useEffect(() => {
    if (!calendarApp) return

    const calendarEvents = toCalendarEvents(events)
    calendarApp.events.set(calendarEvents)
  }, [calendarApp, events])

  useEffect(() => {
    const wrapper = calendarWrapperRef.current
    if (!wrapper) return

    const toOrdinal = (value: number) => {
      const mod100 = value % 100
      if (mod100 >= 11 && mod100 <= 13) return `${value}th`
      const mod10 = value % 10
      if (mod10 === 1) return `${value}st`
      if (mod10 === 2) return `${value}nd`
      if (mod10 === 3) return `${value}rd`
      return `${value}th`
    }

    const toDateKey = (input: Date | string) => {
      const date = input instanceof Date ? input : new Date(input)
      if (Number.isNaN(date.getTime())) return null
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    const attachNewBookingButtons = () => {
      const headers = wrapper.querySelectorAll('.sx__month-grid-day__header')
      headers.forEach((header) => {
        const dateElement = header.querySelector('.sx__month-grid-day__header-date')
        const dayNumber = Number((dateElement?.textContent ?? '').trim())

        const dayContainer = header.closest('.sx__month-grid-day') as HTMLElement | null
        const ariaLabel = dayContainer?.getAttribute('aria-label') ?? ''
        const parsedDate = ariaLabel ? new Date(ariaLabel) : null
        const weekday =
          parsedDate && !Number.isNaN(parsedDate.getTime())
            ? parsedDate.toLocaleDateString('en-US', { weekday: 'short' })
            : ''

        const label = Number.isFinite(dayNumber)
          ? `Book on${weekday ? ` ${weekday}` : ''} ${toOrdinal(dayNumber)}`
          : 'Book on'
        const dateKey = toDateKey(ariaLabel)

        let trigger = header.querySelector('.oc-new-booking-trigger') as HTMLButtonElement | null
        if (!trigger) {
          trigger = document.createElement('button')
          trigger.type = 'button'
          trigger.className =
            'oc-new-booking-trigger rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-900 cursor-pointer'
          header.appendChild(trigger)
        }

        if (trigger.textContent !== label) {
          trigger.textContent = label
        }
        if (trigger.getAttribute('aria-label') !== label) {
          trigger.setAttribute('aria-label', label)
        }

        if (dateKey) {
          trigger.dataset.dateKey = dateKey
        } else {
          delete trigger.dataset.dateKey
        }

        if (pinnedNewBookingDate && dateKey === pinnedNewBookingDate) {
          trigger.classList.add('is-pinned')
        } else {
          trigger.classList.remove('is-pinned')
        }
      })
    }

    attachNewBookingButtons()

    const observer = new MutationObserver(() => {
      attachNewBookingButtons()
    })

    observer.observe(wrapper, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [calendarApp, pinnedNewBookingDate])

  if (!calendarApp) {
    return (
      <div className="min-h-[320px] px-0 py-6 text-sm text-slate-500">
        Loading calendar…
      </div>
    )
  }

  const selectedEvent = selectedEventId
    ? events.find((event) => event.id === selectedEventId) ?? null
    : null

  return (
    <div ref={calendarWrapperRef} className="p-0">
      <style>{`
        .sx__month-grid-day__header {
          position: relative;
        }

        .sx__month-grid-day__header .oc-new-booking-trigger {
          position: absolute;
          top: 2px;
          left: 2px;
          right: 2px;
          z-index: 2;
          opacity: 0;
          pointer-events: none;
          transition: opacity 120ms ease;
        }

        .sx__month-grid-day:hover .oc-new-booking-trigger,
        .sx__month-grid-day:focus-within .oc-new-booking-trigger,
        .sx__month-grid-day .oc-new-booking-trigger.is-pinned {
          opacity: 1;
          pointer-events: auto;
        }
      `}</style>
      <ScheduleXCalendar calendarApp={calendarApp} />
      {newBookingMenu ? (
        <div
          className="oc-new-booking-menu fixed z-50 w-44 rounded-md border border-slate-600 bg-slate-800 p-1.5 shadow-md"
          style={{
            left: Math.max(8, newBookingMenu.anchorRect.left),
            top: newBookingMenu.anchorRect.bottom + 6,
          }}
        >
          <div className="space-y-1">
            <button
              type="button"
              className="flex w-full cursor-pointer rounded px-2 py-1.5 text-left text-xs text-slate-100 transition-colors hover:bg-slate-700 hover:text-white"
              onClick={() => {
                onCreateCalloutAtDate?.(newBookingMenu.date)
                clearPinnedNewBookingTrigger()
                newBookingMenuRef.current = null
                setNewBookingMenu(null)
              }}
            >
              New Callout
            </button>
            <button
              type="button"
              className="flex w-full cursor-pointer rounded px-2 py-1.5 text-left text-xs text-slate-100 transition-colors hover:bg-slate-700 hover:text-white"
              onClick={() => {
                onCreateSessionAtDate?.(newBookingMenu.date)
                clearPinnedNewBookingTrigger()
                newBookingMenuRef.current = null
                setNewBookingMenu(null)
              }}
            >
              New Session
            </button>
          </div>
        </div>
      ) : null}
      {selectedEvent ? (
        <AnchoredPopover
          open={!!anchorRect}
          anchorRect={anchorRect}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedEventId(null)
              setAnchorRect(null)
            }
          }}
          ariaLabel="Selected booking details"
          contentClassName="!w-[28rem] max-w-none border-none bg-transparent p-0 shadow-lg"
        >
          {selectedEvent.booking ? (
            <BookingCard
              booking={selectedEvent.booking}
              onModifyCallout={onModifyCallout}
              onMarkReadyToInvoice={onMarkReadyToInvoice}
              onCancelCallout={onCancelCallout}
              getAuthorDisplayName={getAuthorDisplayName}
            />
          ) : (
            <div className="space-y-2 rounded-md border border-slate-200 bg-white p-4 text-sm">
              <div className="font-medium">
                {selectedEvent.title}
              </div>
              {selectedEvent.date ? (
                <p className="text-xs text-slate-600">
                  <span>{selectedEvent.date}</span>
                  {selectedEvent.startTime ? (
                    <>
                      <span className="mx-1">•</span>
                      <span>
                        {selectedEvent.startTime}
                        {selectedEvent.endTime
                          ? ` – ${selectedEvent.endTime}`
                          : ''}
                      </span>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
          )}
        </AnchoredPopover>
      ) : null}
    </div>
  )
}
